import Phaser from 'phaser';

const PIXEL_SCALE = 50;

export default class BotController {
  constructor(gameScene) {
    this.game = gameScene;
    this.active = false;
  }

  reset() {
    this.active = false;
    if (this.game.botAnimTween) {
      this.game.botAnimTween.stop();
      this.game.botAnimTween = null;
    }
  }

  takeTurn() {
    if (this.game.gameOver) return;
    this.active = true;

    // Block player from tapping during bot animation
    this.game.canDrop = false;

    const targetX     = this._computeTargetX();
    const targetAngle = Phaser.Math.FloatBetween(0, Math.PI * 2);
    const duration    = Phaser.Math.Between(1000, 1500);

    const tweenObj = { x: this.game.aimX };

    this.game.botAnimTween = this.game.tweens.add({
      targets: tweenObj,
      x: targetX,
      duration,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this.game.aimX = tweenObj.x;
        // Smoothly rotate preview toward target angle
        this.game.previewAngle = Phaser.Math.Linear(
          this.game.previewAngle,
          targetAngle,
          0.04
        );
        if (this.game.previewImage) {
          this.game.previewImage.setRotation(this.game.previewAngle);
        }
      },
      onComplete: () => {
        if (this.game.gameOver) return;
        this.game.botAnimTween = null;
        this.game.previewAngle = targetAngle;
        // Re-enable canDrop so botDrop() passes its guard, then immediately drop
        this.game.canDrop = true;
        this.game.botDrop();
      },
    });
  }

  _computeTargetX() {
    const bodies    = this.game.animalBodies;
    const platformX = this.game.platformX;
    const platformW = this.game.platformW;
    const half      = platformW / 2;

    if (bodies.length === 0) {
      return platformX + Phaser.Math.Between(-40, 40);
    }

    // First bot turn (only 1 animal on the platform): aim at the emptier half
    // so the bot spreads out rather than stacking straight on top.
    if (bodies.length === 1) {
      const animalX  = bodies[0].rigidBody.translation().x * PIXEL_SCALE;
      const deadZone = platformW * 0.15; // within 15% of center = treat as centered

      if (Math.abs(animalX - platformX) < deadZone) {
        // Animal is near center — pick anywhere on the platform randomly
        return Phaser.Math.Between(platformX - half + 20, platformX + half - 20);
      } else if (animalX < platformX) {
        // Animal is on the left half — aim somewhere on the right half
        return Phaser.Math.Between(platformX + 20, platformX + half - 20);
      } else {
        // Animal is on the right half — aim somewhere on the left half
        return Phaser.Math.Between(platformX - half + 20, platformX - 20);
      }
    }

    // Normal rounds: aim near center of mass with a small random offset
    const avgX = bodies.reduce((sum, { rigidBody }) => {
      return sum + rigidBody.translation().x * PIXEL_SCALE;
    }, 0) / bodies.length;

    const offset = Phaser.Math.Between(-40, 40);

    return Phaser.Math.Clamp(
      avgX + offset,
      platformX - half - 20,
      platformX + half + 20
    );
  }
}
